import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { useApp } from '../../context';
import Modal from '../shared/Modal';

// Fiscal year runs 1 Dec – 30 Nov. Q1 = Dec–Feb, Q2 = Mar–May, Q3 = Jun–Aug, Q4 = Sep–Nov.
const QUARTER_MONTHS = { 1: 'Dec–Feb', 2: 'Mar–May', 3: 'Jun–Aug', 4: 'Sep–Nov' };

// Fiscal month index of a calendar month: Dec=1, Jan=2 … Nov=12
const fiscalMonthIndex = (calMonth) => (calMonth % 12) + 1;

function pctChange(curr, prev) {
  if (prev == null || prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

function Trend({ pct, invert = false }) {
  if (pct == null || !isFinite(pct)) return <span className="text-[9px] text-gray-300">—</span>;
  const up = pct >= 0;
  const good = invert ? !up : up; // for costs, down is good
  return (
    <span className={`text-[9px] font-bold whitespace-nowrap ${good ? 'text-emerald-600' : 'text-red-600'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function compact(v) {
  if (v == null || isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1000000) return `£${(v / 1000000).toFixed(1)}m`;
  if (a >= 1000) return `£${Math.round(v / 1000)}k`;
  return `£${Math.round(v)}`;
}

// Quarter start calendar month (1-based): Q1=Dec(12), Q2=Mar(3), Q3=Jun(6), Q4=Sep(9)
const Q_START_MONTH = { 1: 12, 2: 3, 3: 6, 4: 9 };

function quarterPaceRate(quarter, fy) {
  const startMonth = Q_START_MONTH[quarter];
  const startYear  = startMonth === 12 ? fy - 1 : fy;
  const start      = new Date(startYear, startMonth - 1, 1);
  const endOfQ     = new Date(startYear, startMonth + 2, 0); // last day of 3rd month
  const totalDays  = Math.floor((endOfQ - start) / 86400000) + 1;
  const today      = new Date();
  const elapsed    = Math.max(1, Math.min(totalDays, Math.floor((today - start) / 86400000) + 1));
  return { totalDays, elapsed };
}

function fyPaceRate(fy) {
  const start     = new Date(fy - 1, 11, 1); // Dec 1
  const end       = new Date(fy, 10, 30);     // Nov 30
  const totalDays = Math.floor((end - start) / 86400000) + 1;
  const today     = new Date();
  const elapsed   = Math.max(1, Math.min(totalDays, Math.floor((today - start) / 86400000) + 1));
  return { totalDays, elapsed };
}

// Dashed "coming soon" card for metrics that need data the platform doesn't hold yet.
function ComingSoonCard({ title, detail }) {
  return (
    <div className="border-2 border-dashed border-gray-200 rounded p-2.5 bg-gray-50/50">
      <div className="flex items-center gap-1.5 mb-1">
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        <span className="text-[9px] font-bold text-gray-500 uppercase">{title}</span>
        <span className="text-[7px] font-bold text-sky-600 bg-sky-100 px-1 py-0.5 rounded ml-auto">COMING SOON</span>
      </div>
      <div className="text-[8px] text-gray-400 leading-relaxed">{detail}</div>
    </div>
  );
}

export default function ExecutiveSummaryPage() {
  const { currentUser } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allQuarters, setAllQuarters] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [journals, setJournals] = useState([]);
  const [journalForm, setJournalForm] = useState({ month: '', payroll_total: '', overheads_total: '', notes: '' });
  const [journalError, setJournalError] = useState(null);
  const [saving, setSaving] = useState(false);

  const roles = (currentUser?.user_role || '').split(',').map(r => r.trim());
  const canEditJournals = roles.some(r => ['admin', 'finance'].includes(r));

  const load = useCallback(() => {
    api.get('executive-summary')
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const openJournal = async () => {
    setJournalError(null);
    setJournalForm({ month: data?.current_month || '', payroll_total: '', overheads_total: '', notes: '' });
    setJournalOpen(true);
    try { setJournals(await api.get('finance-journals') || []); } catch { setJournals([]); }
  };

  const pickJournal = (j) => setJournalForm({
    month: j.month,
    payroll_total: j.payroll_total ?? '',
    overheads_total: j.overheads_total ?? '',
    notes: j.notes || '',
  });

  const saveJournal = async () => {
    setJournalError(null);
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(journalForm.month)) { setJournalError('Month must be in YYYY-MM format'); return; }
    setSaving(true);
    try {
      await api.post('finance-journals', {
        month: journalForm.month,
        payroll_total: journalForm.payroll_total === '' ? null : parseFloat(journalForm.payroll_total),
        overheads_total: journalForm.overheads_total === '' ? null : parseFloat(journalForm.overheads_total),
        notes: journalForm.notes || null,
      });
      setJournals(await api.get('finance-journals') || []);
      load();
    } catch (e) {
      setJournalError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteJournal = async (j) => {
    try {
      await api.delete(`finance-journals/${j.id}`);
      setJournals(await api.get('finance-journals') || []);
      load();
    } catch (e) { setJournalError(e.message); }
  };

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>;
  if (error) return <div className="bg-white border border-red-200 rounded p-8 text-center text-[10px] text-red-500">{error}</div>;
  if (!data) return null;

  const { months, quarters, years, order_book, cash_flow, coverage, current_fy, current_quarter, current_month } = data;

  // ── FY-to-date vs the same point last year ────────────────────────────────
  const fmNow = fiscalMonthIndex(Number(current_month.slice(5, 7)));
  const fytd = months.filter(m => m.fy === current_fy && m.month <= current_month);
  const priorFytd = months.filter(m => m.fy === current_fy - 1 && fiscalMonthIndex(Number(m.month.slice(5, 7))) <= fmNow);
  const sum = (rows, k) => rows.reduce((s, r) => s + (r[k] || 0), 0);

  const turnoverFytd = sum(fytd, 'income_invoiced');
  const turnoverPrior = sum(priorFytd, 'income_invoiced');
  const cashFytd = sum(fytd, 'cash_in');
  const outFytd = sum(fytd, 'outgoings_total');
  const outPrior = sum(priorFytd, 'outgoings_total');
  const netFytd = turnoverFytd - outFytd;
  const netPrior = turnoverPrior - sum(priorFytd, 'outgoings_total');

  // ── Quarter table rows, newest first, with QoQ and YoY deltas ─────────────
  const qRows = quarters.map((q, i) => {
    const prev = i > 0 ? quarters[i - 1] : null;
    const yoy = quarters.find(p => p.fy === q.fy - 1 && p.quarter === q.quarter);
    return {
      ...q,
      qoq_pct: pctChange(q.income_invoiced, prev?.income_invoiced),
      yoy_pct: pctChange(q.income_invoiced, yoy?.income_invoiced),
      is_current: q.fy === current_fy && q.quarter === current_quarter,
    };
  }).reverse();
  const visibleQRows = allQuarters ? qRows : qRows.slice(0, 8);

  // ── Year table rows, newest first ─────────────────────────────────────────
  const yRows = years.map((y, i) => {
    const prev = i > 0 ? years[i - 1] : null;
    return {
      ...y,
      yoy_pct: pctChange(y.income_invoiced, prev?.income_invoiced),
      margin: y.income_invoiced > 0 ? (y.net / y.income_invoiced) * 100 : null,
      is_current: y.fy === current_fy,
    };
  }).reverse();

  // ── Chart data: last 8 quarters, oldest → newest ──────────────────────────
  const chartQ = quarters.slice(-8);
  const chartMax = Math.max(...chartQ.map(q => Math.max(q.income_invoiced, q.outgoings_total)), 1);

  // ── Budget vs actual / people metrics: quarters that have the data ────────
  const budgetRows = visibleQRows.filter(q => q.budget_revenue != null || q.budget_costs != null);
  const peopleRows = visibleQRows.filter(q => q.avg_headcount != null && q.avg_headcount > 0);
  const anyUtilisation = peopleRows.some(q => q.utilisation_pct != null);

  // ── Cash flow chart scale ─────────────────────────────────────────────────
  const cfMax = Math.max(...(cash_flow?.weeks || []).map(w => Math.max(w.inflow, w.outflow)), 1);
  const cfNet = (cash_flow?.inflow_total || 0) - (cash_flow?.outflow_total || 0);

  const payrollPartial = coverage.salaries_recorded > 0 && coverage.salaries_recorded < coverage.active_people;
  const payrollMissing = coverage.salaries_recorded === 0 && !coverage.months_with_actual_payroll;

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Executive Summary</h2>
          <div className="text-[9px] text-gray-500">
            Known incomings vs outgoings · Financial year 1 Dec – 30 Nov (Q1 Dec–Feb · Q2 Mar–May · Q3 Jun–Aug · Q4 Sep–Nov)
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canEditJournals && (
            <button onClick={openJournal} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
              + Monthly Journal
            </button>
          )}
          <div className="text-right">
            <div className="text-[8px] text-gray-500 uppercase font-bold">Current Period</div>
            <div className="text-[11px] font-extrabold text-navy">Q{current_quarter} FY{String(current_fy - 1).slice(-2)}/{String(current_fy).slice(-2)}</div>
          </div>
        </div>
      </div>

      {months.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">
          No invoiced milestones or recorded spend yet — once invoices and POs are recorded they will roll up here by fiscal quarter and year.
        </div>
      ) : (
        <>
          {/* FY-to-date KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Turnover FYTD</div>
              <div className="text-lg font-extrabold text-navy">{formatMoney(turnoverFytd)}</div>
              <div className="text-[8px] text-gray-400">vs same point last FY <Trend pct={pctChange(turnoverFytd, turnoverPrior)} /></div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Cash Collected FYTD</div>
              <div className="text-lg font-extrabold text-emerald-500">{formatMoney(cashFytd)}</div>
              <div className="text-[8px] text-gray-400">paid milestones</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Known Outgoings FYTD</div>
              <div className="text-lg font-extrabold text-amber-600">{formatMoney(outFytd)}</div>
              <div className="text-[8px] text-gray-400">vs same point last FY <Trend pct={pctChange(outFytd, outPrior)} invert /></div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Net (Known) FYTD</div>
              <div className={`text-lg font-extrabold ${netFytd >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatMoney(netFytd)}</div>
              <div className="text-[8px] text-gray-400">vs same point last FY <Trend pct={pctChange(netFytd, netPrior)} /></div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-2">
              <div className="text-[8px] text-gray-500 uppercase font-bold">Order Book</div>
              <div className="text-lg font-extrabold text-sky-600">{formatMoney(order_book.amount)}</div>
              <div className="text-[8px] text-gray-400">{order_book.count} contracted milestones · {compact(order_book.next_90_days)} due in 90 days</div>
            </div>
          </div>

          {/* Quarterly turnover vs outgoings chart */}
          <div className="bg-white border border-gray-200 rounded p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] font-bold text-navy">Turnover vs Outgoings by Quarter</h3>
              <div className="flex items-center gap-3 text-[8px] text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: '#1B2238' }} /> Turnover (invoiced)</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: '#D97706' }} /> Known outgoings</span>
              </div>
            </div>
            <div className="flex items-end gap-2" style={{ height: 110 }}>
              {chartQ.map(q => {
                const hIn = Math.max(3, (q.income_invoiced / chartMax) * 84);
                const hOut = Math.max(3, (q.outgoings_total / chartMax) * 84);
                return (
                  <div key={`${q.fy}-${q.quarter}`} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="text-[7px] font-semibold" style={{ color: q.net >= 0 ? '#10B981' : '#EF4444' }}>
                      {q.net >= 0 ? '+' : ''}{compact(q.net)}
                    </div>
                    <div className="w-full flex items-end justify-center gap-0.5" style={{ height: 84 }}>
                      <div style={{ width: '38%', height: hIn, background: '#1B2238', borderRadius: '2px 2px 0 0', opacity: 0.85 }} title={`Turnover ${formatMoney(q.income_invoiced)}`} />
                      <div style={{ width: '38%', height: hOut, background: '#D97706', borderRadius: '2px 2px 0 0', opacity: 0.7 }} title={`Outgoings ${formatMoney(q.outgoings_total)}`} />
                    </div>
                    <div className="text-[7px] text-gray-500 font-semibold whitespace-nowrap">{q.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quarter-by-quarter review */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden mb-4">
            <div className="px-3 py-2 border-b border-gray-200 bg-navy/5 flex items-center justify-between">
              <span className="text-[11px] font-bold text-navy">Quarter-by-Quarter Review</span>
              {qRows.length > 8 && (
                <button onClick={() => setAllQuarters(a => !a)} className="text-[8px] font-semibold text-gray-500 hover:text-navy">
                  {allQuarters ? 'Show recent only' : `Show all ${qRows.length} quarters`}
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Quarter</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Turnover</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">QoQ</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">YoY</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Cash In</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Outgoings</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Net (Known)</th>
                </tr></thead>
                <tbody>
                  {visibleQRows.map(q => {
                    const { totalDays, elapsed } = q.is_current ? quarterPaceRate(q.quarter, q.fy) : {};
                    const qPace = q.is_current && q.income_invoiced > 0 ? q.income_invoiced * (totalDays / elapsed) : null;
                    return (
                    <tr key={`${q.fy}-${q.quarter}`} className={`border-b border-gray-100 hover:bg-gold/5 ${q.is_current ? 'bg-sky-50/50' : ''}`}>
                      <td className="px-2 py-1">
                        <span className="text-[10px] font-bold text-navy">{q.label}</span>
                        <span className="text-[8px] text-gray-400 ml-1.5">{QUARTER_MONTHS[q.quarter]}</span>
                        {q.is_current && <span className="text-[7px] font-bold text-sky-600 bg-sky-100 px-1 py-0.5 rounded ml-1.5">CURRENT</span>}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <div className="text-[10px] font-semibold text-navy">{formatMoney(q.income_invoiced)}</div>
                        {qPace && <div className="text-[7px] text-sky-600 font-semibold">on pace ~{compact(qPace)}</div>}
                      </td>
                      <td className="px-2 py-1 text-right"><Trend pct={q.qoq_pct} /></td>
                      <td className="px-2 py-1 text-right"><Trend pct={q.yoy_pct} /></td>
                      <td className="px-2 py-1 text-[10px] text-right text-emerald-600">{formatMoney(q.cash_in)}</td>
                      <td className="px-2 py-1 text-[10px] text-right text-amber-700">{formatMoney(q.outgoings_total)}</td>
                      <td className={`px-2 py-1 text-[10px] font-semibold text-right ${q.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatMoney(q.net)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-1.5 text-[8px] text-gray-400 border-t border-gray-100">
              QoQ compares with the previous quarter; YoY with the same quarter last financial year. <b>On pace</b> projects the current quarter's invoiced figure to a full-quarter run-rate based on days elapsed — QoQ/YoY trends will appear depressed until the quarter closes.
            </div>
          </div>

          {/* Year-by-year review */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden mb-4">
            <div className="px-3 py-2 border-b border-gray-200 bg-navy/5">
              <span className="text-[11px] font-bold text-navy">Year-by-Year Review</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Financial Year</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Turnover</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">YoY</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Cash In</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Outgoings</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Net (Known)</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Net Margin</th>
                </tr></thead>
                <tbody>
                  {yRows.map(y => {
                    const { totalDays, elapsed } = y.is_current ? fyPaceRate(y.fy) : {};
                    const yPace = y.is_current && y.income_invoiced > 0 ? y.income_invoiced * (totalDays / elapsed) : null;
                    return (
                    <tr key={y.fy} className={`border-b border-gray-100 hover:bg-gold/5 ${y.is_current ? 'bg-sky-50/50' : ''}`}>
                      <td className="px-2 py-1">
                        <span className="text-[10px] font-bold text-navy">{y.label}</span>
                        <span className="text-[8px] text-gray-400 ml-1.5">Dec {y.fy - 1} – Nov {y.fy}</span>
                        {y.is_current && <span className="text-[7px] font-bold text-sky-600 bg-sky-100 px-1 py-0.5 rounded ml-1.5">IN PROGRESS</span>}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <div className="text-[10px] font-bold text-navy">{formatMoney(y.income_invoiced)}</div>
                        {yPace && <div className="text-[7px] text-sky-600 font-semibold">on pace ~{compact(yPace)}</div>}
                      </td>
                      <td className="px-2 py-1 text-right"><Trend pct={y.yoy_pct} /></td>
                      <td className="px-2 py-1 text-[10px] text-right text-emerald-600">{formatMoney(y.cash_in)}</td>
                      <td className="px-2 py-1 text-[10px] text-right text-amber-700">{formatMoney(y.outgoings_total)}</td>
                      <td className={`px-2 py-1 text-[10px] font-bold text-right ${y.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatMoney(y.net)}</td>
                      <td className={`px-2 py-1 text-[10px] font-semibold text-right ${y.margin == null ? 'text-gray-300' : y.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {y.margin == null ? '—' : `${y.margin.toFixed(1)}%`}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Outgoings breakdown */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden mb-4">
            <div className="px-3 py-2 border-b border-gray-200 bg-navy/5">
              <span className="text-[11px] font-bold text-navy">Outgoings Breakdown by Quarter</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Quarter</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">PO Spend (Projects)</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">PO Spend (Internal)</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Overhead Invoices</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Journal Overheads</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Contractor Labour</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Payroll</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Total</th>
                </tr></thead>
                <tbody>
                  {visibleQRows.map(q => (
                    <tr key={`${q.fy}-${q.quarter}`} className="border-b border-gray-100 hover:bg-gold/5">
                      <td className="px-2 py-1 text-[10px] font-bold text-navy">{q.label}</td>
                      <td className="px-2 py-1 text-[10px] text-right">{formatMoney(q.spend_po_project)}</td>
                      <td className="px-2 py-1 text-[10px] text-right">{formatMoney(q.spend_po_internal)}</td>
                      <td className="px-2 py-1 text-[10px] text-right">{formatMoney(q.spend_overhead_invoices)}</td>
                      <td className="px-2 py-1 text-[10px] text-right">{formatMoney(q.spend_journal_overheads)}</td>
                      <td className="px-2 py-1 text-[10px] text-right">{formatMoney(q.spend_contractor)}</td>
                      <td className="px-2 py-1 text-[10px] text-right text-gray-500">
                        {payrollMissing ? '—' : formatMoney(q.payroll_estimate)}
                        {q.payroll_actual_months > 0 && <span className="text-[7px] font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded ml-1" title={`${q.payroll_actual_months} month(s) use keyed journal actuals`}>ACTUAL ×{q.payroll_actual_months}</span>}
                      </td>
                      <td className="px-2 py-1 text-[10px] font-bold text-right text-amber-700">{formatMoney(q.outgoings_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-1.5 text-[8px] text-gray-400 border-t border-gray-100">
              Payroll uses keyed monthly journal actuals where available ({coverage.months_with_actual_payroll || 0} month{coverage.months_with_actual_payroll === 1 ? '' : 's'} so far), salary-based estimate otherwise. Journal overheads are costs keyed monthly that don't pass through POs or the Accounts Inbox.
            </div>
          </div>

          {/* Budget vs actual */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden mb-4">
            <div className="px-3 py-2 border-b border-gray-200 bg-navy/5">
              <span className="text-[11px] font-bold text-navy">Budget vs Actual by Quarter</span>
            </div>
            {budgetRows.length === 0 ? (
              <div className="p-4 text-center text-[9px] text-gray-400">
                No budget months entered yet — add months on the <b>Budget</b> page and they'll be compared against actuals here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Quarter</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Budget Revenue</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Actual Turnover</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Variance</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Budget Costs</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Actual Outgoings</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Variance</th>
                  </tr></thead>
                  <tbody>
                    {budgetRows.map(q => (
                      <tr key={`${q.fy}-${q.quarter}`} className="border-b border-gray-100 hover:bg-gold/5">
                        <td className="px-2 py-1 text-[10px] font-bold text-navy">{q.label}</td>
                        <td className="px-2 py-1 text-[10px] text-right text-gray-500">{formatMoney(q.budget_revenue)}</td>
                        <td className="px-2 py-1 text-[10px] font-semibold text-right text-navy">{formatMoney(q.income_invoiced)}</td>
                        <td className="px-2 py-1 text-right"><Trend pct={pctChange(q.income_invoiced, q.budget_revenue)} /></td>
                        <td className="px-2 py-1 text-[10px] text-right text-gray-500">{formatMoney(q.budget_costs)}</td>
                        <td className="px-2 py-1 text-[10px] text-right text-amber-700">{formatMoney(q.outgoings_total)}</td>
                        <td className="px-2 py-1 text-right"><Trend pct={pctChange(q.outgoings_total, q.budget_costs)} invert /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* People & productivity */}
          <div className="bg-white border border-gray-200 rounded overflow-hidden mb-4">
            <div className="px-3 py-2 border-b border-gray-200 bg-navy/5">
              <span className="text-[11px] font-bold text-navy">People & Productivity by Quarter</span>
            </div>
            {peopleRows.length === 0 ? (
              <div className="p-4 text-center text-[9px] text-gray-400">
                Needs joining/leaving dates on People records to derive headcount per quarter.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Quarter</th>
                      <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Avg Headcount (FTE)</th>
                      <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Billable Hours</th>
                      <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Utilisation</th>
                      <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Revenue / Head (Annualised)</th>
                    </tr></thead>
                    <tbody>
                      {peopleRows.map(q => (
                        <tr key={`${q.fy}-${q.quarter}`} className="border-b border-gray-100 hover:bg-gold/5">
                          <td className="px-2 py-1 text-[10px] font-bold text-navy">{q.label}</td>
                          <td className="px-2 py-1 text-[10px] text-right">{q.avg_headcount}</td>
                          <td className="px-2 py-1 text-[10px] text-right">{q.billable_hours ? Math.round(q.billable_hours).toLocaleString('en-GB') : '—'}</td>
                          <td className={`px-2 py-1 text-[10px] font-semibold text-right ${q.utilisation_pct == null ? 'text-gray-300' : q.utilisation_pct >= 60 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {q.utilisation_pct == null ? '—' : `${q.utilisation_pct.toFixed(1)}%`}
                          </td>
                          <td className="px-2 py-1 text-[10px] font-semibold text-right text-navy">{q.revenue_per_head_annualised == null ? '—' : formatMoney(q.revenue_per_head_annualised)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-3 py-1.5 text-[8px] text-gray-400 border-t border-gray-100">
                  Utilisation = billable timesheet hours ÷ capacity, where capacity is each person's contracted hours on the days they work, less England and Wales bank holidays, closures and approved leave{anyUtilisation ? '. Only as reliable as timesheet discipline.' : '. No billable timesheet hours recorded yet.'} Revenue per head annualises each quarter's turnover (×4) for comparability.
                </div>
              </>
            )}
          </div>

          {/* Cash view — next 13 weeks */}
          {cash_flow && (
            <div className="bg-white border border-gray-200 rounded p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-bold text-navy">Expected Cash Movements — Next 13 Weeks</h3>
                <div className="flex items-center gap-3 text-[8px] text-gray-500">
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: '#10B981' }} /> Expected in</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: '#EF4444' }} /> Expected out</span>
                  <span className="text-[7px] font-bold text-sky-600 bg-sky-100 px-1 py-0.5 rounded">BANK FEED — COMING SOON</span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <div className="border border-gray-100 rounded p-1.5">
                  <div className="text-[7px] text-gray-500 uppercase font-bold">Expected In</div>
                  <div className="text-[12px] font-extrabold text-emerald-600">{formatMoney(cash_flow.inflow_total)}</div>
                </div>
                <div className="border border-gray-100 rounded p-1.5">
                  <div className="text-[7px] text-gray-500 uppercase font-bold">Expected Out</div>
                  <div className="text-[12px] font-extrabold text-red-500">{formatMoney(cash_flow.outflow_total)}</div>
                </div>
                <div className="border border-gray-100 rounded p-1.5">
                  <div className="text-[7px] text-gray-500 uppercase font-bold">Net Movement</div>
                  <div className={`text-[12px] font-extrabold ${cfNet >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatMoney(cfNet)}</div>
                </div>
                <div className={`border rounded p-1.5 ${cash_flow.overdue_receivables ? 'border-red-200 bg-red-50' : 'border-gray-100'}`}>
                  <div className={`text-[7px] uppercase font-bold ${cash_flow.overdue_receivables ? 'text-red-600' : 'text-gray-500'}`}>Overdue Receivables</div>
                  <div className={`text-[12px] font-extrabold ${cash_flow.overdue_receivables ? 'text-red-600' : 'text-gray-400'}`}>{formatMoney(cash_flow.overdue_receivables)}</div>
                </div>
              </div>
              <div className="flex items-end gap-1" style={{ height: 90 }}>
                {cash_flow.weeks.map((w, i) => {
                  const hIn = Math.max(2, (w.inflow / cfMax) * 64);
                  const hOut = Math.max(2, (w.outflow / cfMax) * 64);
                  return (
                    <div key={w.week_start} className="flex-1 flex flex-col items-center gap-0.5">
                      <div className="text-[6px] font-semibold" style={{ color: w.net >= 0 ? '#10B981' : '#EF4444' }}>{compact(w.net)}</div>
                      <div className="w-full flex items-end justify-center gap-px" style={{ height: 64 }}>
                        <div style={{ width: '40%', height: hIn, background: '#10B981', borderRadius: '2px 2px 0 0', opacity: 0.75 }} title={`In ${formatMoney(w.inflow)}`} />
                        <div style={{ width: '40%', height: hOut, background: '#EF4444', borderRadius: '2px 2px 0 0', opacity: 0.65 }} title={`Out ${formatMoney(w.outflow)}`} />
                      </div>
                      <div className="text-[6px] text-gray-400">{i === 0 ? 'now' : new Date(w.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                    </div>
                  );
                })}
              </div>
              <div className="text-[8px] text-gray-400 mt-2 leading-relaxed">
                Estimates, not a balance: movements only (no bank feed). In = unpaid invoiced milestones by expected pay date (incl. VAT where recorded; overdue items shown in the current week).
                Out = recent overhead supplier invoices (no paid flag yet, so invoices from the last 45 days are assumed unpaid, due +30d), unpaid contractor invoices (+30d), and payroll of {formatMoney(cash_flow.monthly_payroll_assumed)} at each month end.
                {(cash_flow.beyond_window_in > 0 || cash_flow.beyond_window_out > 0) && ` Beyond 13 weeks: ${compact(cash_flow.beyond_window_in)} in / ${compact(cash_flow.beyond_window_out)} out.`}
              </div>
            </div>
          )}

          {/* Coming soon */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            <ComingSoonCard
              title="VAT & Corporation Tax"
              detail="VAT quarter liabilities and corporation tax provisions need accounting-system data — not yet held in this platform."
            />
            <ComingSoonCard
              title="Accounting System Sync"
              detail="Monthly P&L import from Xero/Sage to reconcile these figures with the real books and capture overheads paid by direct debit."
            />
            <ComingSoonCard
              title="Bank Feed & Cash Balance"
              detail="A live bank balance would turn the 13-week cash movements above into a true rolling cash forecast."
            />
          </div>

          {/* Data coverage — what these numbers do and don't include */}
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-[10px] font-bold text-amber-800 mb-1.5">What these numbers do — and don't — include</div>
            <ul className="text-[9px] text-amber-900/80 space-y-1 list-disc pl-4">
              <li><b>Turnover</b> is the value of invoiced milestones (excl. VAT) in the month the formal invoice was raised. Work delivered but not yet invoiced is in the order book figure, not turnover.</li>
              <li><b>Outgoings</b> = PO value (committed supplier spend by order date) + non-PO supplier invoices from the Accounts Inbox + monthly journal overheads + contractor invoices{payrollMissing ? '' : ' + payroll'}. PO-matched supplier invoices are not double-counted.</li>
              {payrollMissing && <li><b>Payroll is missing entirely</b> — no salaries recorded and no monthly journals keyed yet, so staff cost (typically the largest cost line) is not in these outgoings. Use the <b>+ Monthly Journal</b> button to key actual payroll per month.</li>}
              {!payrollMissing && (coverage.months_with_actual_payroll || 0) > 0 && <li><b>Payroll</b> uses keyed journal actuals for {coverage.months_with_actual_payroll} month{coverage.months_with_actual_payroll === 1 ? '' : 's'}; other months fall back to the salary estimate (annual ÷ 12{payrollPartial ? `, from ${coverage.salaries_recorded} of ${coverage.active_people} active people with a recorded salary` : ''}) which excludes employer NI, pension and bonuses.</li>}
              {!payrollMissing && !(coverage.months_with_actual_payroll || 0) && <li><b>Payroll is an estimate</b> — recorded annual salaries ÷ 12 per month of employment{payrollPartial ? ` (only ${coverage.salaries_recorded} of ${coverage.active_people} active people have a salary recorded)` : ''}. Employer NI (~13.8%), pension and bonuses are not included. Key actual monthly payroll via <b>+ Monthly Journal</b> to replace the estimate.</li>}
              <li>Not captured anywhere in this platform: VAT and corporation tax, bank/cash balances, staff expense claims, depreciation, and financing. Treat "Net (Known)" as a directional operating view, not statutory profit.</li>
            </ul>
          </div>
        </>
      )}

      {/* Monthly journal modal */}
      {journalOpen && (
        <Modal
          title="Monthly Finance Journal"
          onClose={() => setJournalOpen(false)}
          footer={<>
            <button onClick={() => setJournalOpen(false)} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Close</button>
            <button onClick={saveJournal} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-50">{saving ? 'Saving…' : 'Save Month'}</button>
          </>}
        >
          <div className="text-[9px] text-gray-500 mb-2 leading-relaxed">
            Key figures the platform can't see: <b>actual payroll</b> (gross + employer NI + pension — replaces the salary estimate for that month) and <b>overheads paid outside POs / the Accounts Inbox</b> (e.g. rent or insurance by direct debit).
          </div>
          {journalError && <div className="text-[9px] text-red-600 font-semibold mb-2">{journalError}</div>}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Month (YYYY-MM)</label>
              <input className="form-input" value={journalForm.month} onChange={e => setJournalForm(f => ({ ...f, month: e.target.value }))} placeholder="2026-06" />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Actual Payroll Total (£)</label>
              <input className="form-input" type="number" step="0.01" value={journalForm.payroll_total} onChange={e => setJournalForm(f => ({ ...f, payroll_total: e.target.value }))} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Other Overheads (£)</label>
              <input className="form-input" type="number" step="0.01" value={journalForm.overheads_total} onChange={e => setJournalForm(f => ({ ...f, overheads_total: e.target.value }))} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
              <input className="form-input" value={journalForm.notes} onChange={e => setJournalForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          {journals.length > 0 && (
            <div className="border border-gray-100 rounded overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Month</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Payroll</th>
                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Overheads</th>
                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">By</th>
                  <th className="px-2 py-1" />
                </tr></thead>
                <tbody>
                  {journals.slice(0, 12).map(j => (
                    <tr key={j.id} className="border-b border-gray-100 hover:bg-gold/5 cursor-pointer" onClick={() => pickJournal(j)} title="Click to load into the form">
                      <td className="px-2 py-1 text-[9px] font-bold">{j.month}</td>
                      <td className="px-2 py-1 text-[9px] text-right">{j.payroll_total != null ? formatMoney(j.payroll_total) : '—'}</td>
                      <td className="px-2 py-1 text-[9px] text-right">{j.overheads_total != null ? formatMoney(j.overheads_total) : '—'}</td>
                      <td className="px-2 py-1 text-[8px] text-gray-400">{j.updated_by_name || '—'}</td>
                      <td className="px-2 py-1 text-right">
                        <button onClick={(e) => { e.stopPropagation(); deleteJournal(j); }} className="text-[8px] font-semibold text-red-400 hover:text-red-600">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
